import { Collapse, Flex, Text, theme } from '@/components';
import { useProjectsSearchStore } from '@/features/search/_stores/projects-search-store';
import { useForm } from '@/hooks';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFundSheetAnalysisPageSearchQueryStates } from '../_hooks/search-query-states';
import {
  type KsAnalysisSearchFormType,
  KsAnalysisSearchSchema,
} from '../_models/ks-analysis-search';
import { SearchForm } from './SearchForm';
import { KsAggregationTags } from './tags/KsAggregationTags';
import { KsAnalysisSearchSearchFieldTags } from './tags/KsAnalysisSearchSearchFieldTags';

export const KsAnalysisSearchAccordion: FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const { searchQueryStates } = useFundSheetAnalysisPageSearchQueryStates();
  const { cascaderTargetIds } = useProjectsSearchStore();
  const { openBasicAccordion, setOpenBasicAccordion } =
    useProjectsSearchStore();
  // 支出先の検索ワードをリセットするため。booleanだと一瞬の変化で検知できないため数値で比較
  const [resetCount, setResetCount] = useState(0);

  const defaultValues = useMemo<KsAnalysisSearchFormType>(() => {
    return {
      ...searchQueryStates,
      // sheetType / fundSheetType はルート（/analysis/fund-sheet）から決まる読み取り専用値。
      // 共通検索フィールド（KeywordColField 等）が useWatch('sheetType') / useWatch('fundSheetType') で
      // 表示判定するため、フォーム状態にも反映させる。
      sheetType: 'KS',
      fundSheetType: 'standard',
      fiscalYear: dayjs().year(searchQueryStates.fiscalYear),
      nestedOrganizationIds: cascaderTargetIds.organizationIds ?? null,
      // URL の `total` センチネル（合計のみ）はフォームでは null（軸タグ非表示・セレクト空）にする。
      // それ以外（organization 等）はそのまま反映。URL は withDefault で初期表示が organization になる（RS-4424）。
      analysisAxis:
        searchQueryStates.analysisAxis === 'total'
          ? null
          : searchQueryStates.analysisAxis,
    };
  }, [cascaderTargetIds, searchQueryStates]);

  const formMethod = useForm<KsAnalysisSearchFormType>({
    resolver: zodResolver(KsAnalysisSearchSchema),
  });
  // react-hook-form の useForm 戻り値は毎レンダリングで同一参照を保証していないため、
  // useEffect の依存配列に formMethod 全体を入れると不要な再実行が起きる。
  // 必要な関数だけ取り出して依存に含める。
  const { reset } = formMethod;

  const onResetPayment = useCallback(() => {
    setResetCount((v) => v + 1);
  }, []);

  // reset のトリガーと reset に渡す値を「同一スナップショット」に揃えるため、
  // useLocation の URL（即時更新）ではなく defaultValues（searchQueryStates / cascaderTargetIds
  // から導出）の変化で発火させる。検索＝URL 変更でこのアコーディオンは再マウントされ得るが、
  // analysisAxis は URL を単一の真実にしたため、再マウント後も URL の値（total=合計のみ / organization 等）が
  // そのまま反映される（RS-4424）。
  const prevDefaultValues = useRef<KsAnalysisSearchFormType | null>(null);
  useEffect(() => {
    if (prevDefaultValues.current !== defaultValues) {
      reset(defaultValues);
      prevDefaultValues.current = defaultValues;
    }
  }, [defaultValues, reset]);

  return (
    <FormProvider {...formMethod}>
      <Collapse
        accordion
        activeKey={openBasicAccordion ? '1' : undefined}
        expandIcon={({ isActive }) =>
          isActive ? (
            <DownOutlined style={{ color: token.colorPrimary, fontSize: 12 }} />
          ) : (
            <RightOutlined
              style={{ color: token.colorPrimary, fontSize: 12 }}
            />
          )
        }
        onChange={(e) => {
          setOpenBasicAccordion(!!e.length);
        }}
        style={{
          background: token.colorBgBase,
          borderRadius: 0,
          minHeight: 80,
        }}
        items={[
          {
            id: 'filter-fund-sheet-analysis-panel',
            key: '1',
            style: { borderRadius: 0 },
            label: (
              <>
                <Text color="primary" text={t('search-condition')} />
                {!openBasicAccordion && (
                  <Flex vertical gap={8}>
                    <KsAnalysisSearchSearchFieldTags
                      onResetPayment={onResetPayment}
                    />
                    <KsAggregationTags />
                  </Flex>
                )}
              </>
            ),
            children: (
              <SearchForm
                onResetPayment={onResetPayment}
                resetCount={resetCount}
              />
            ),
          },
        ]}
      />
    </FormProvider>
  );
};
